import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK, WORM } from '@/game/config';
import { scriptFontFamily } from '@/game/fonts';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { wordIconKey, wordIconUrl } from '@/game/wordIcons';
import { getLettersPack } from '@/data/packs/letters';
import { getPictureWordsPool } from '@/data/pools';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { shouldShowBuyPrompt } from '@/game/promo';
import {
  playChompSound, playThudSound, playGateSound, playJourneyFanfare, speakRulePrompt,
} from '@/game/audio';
import { devFlag } from '@/game/dev';
import { classesOf, type LetterClass } from '@/game/cat/membership';
import { letterProgress, metClassIds, isKnown } from '@/game/cat/store';
import {
  newWorm, advance, steer, steerAngle, beadPositions, selfHit, dangerAhead, shrinkTo, dist,
  reachedTarget, directDist, inside, outsideBy, nearestInside, safeReturnAngle, leashTurnRate,
  type Worm, type Pt,
} from '@/game/worm/step';
import { dealWormBoard } from '@/game/worm/deal';
import { bite, nextGlyph, tileAt, isComplete, spelledSoFar, type WormBoard } from '@/game/worm/order';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';

interface WormInit {
  lang: string;
  /** Dev only (`?worm=4`): start at a higher rung, where the picture stops showing its word. */
  startRung?: number;
}

/** The run's colour ramp (specs/space-worm-mode.md §14): warm at the head, cool down the body. */
const RAMP = [0xff8a3d, 0xff5d73, 0xc86dd7, 0x6aa6ff, 0x46d6b4];
/** The head is the ramp's warm first colour and never carries a letter — it is the pen (§5.6). */
const HEAD = 0xffd23f;
const BODY_BASE = 0x8fa6c8;
const SKY = 0x05060d;
const TILE_FILL = 0x181a33;
const TILE_RING = 0x5c6bc0;
const NEXT_RING = 0x7fe7ff;
const DANGER = 0xff4d5e;
/**
 * How far the finger must travel to take the wheel back after a knot respawn (§5.5.1).
 *
 * Above the jitter a finger resting on a screen sends, well below a deliberate point somewhere.
 */
const STEER_GATE = 60;

/**
 * Space Worm — the red-Sun treat (specs/space-worm-mode.md).
 *
 * A worm crawls a dark starfield eating the letters of a pictured word, in the order they spell it.
 * Every right letter becomes a coloured bead with that letter on it — the body IS the word — and a
 * wrong one costs a 📚 book **and the whole word**, which dissolves into stars and is replaced.
 * Touching its own body ends the run.
 *
 * Three things here are deliberate and none of them may grow back:
 *  - **no clock** (§5.7) — the worm's own body is the clock;
 *  - **no speed-up as it eats**, because that would make reading correctly the thing that makes
 *    reading harder;
 *  - **no walls** (§5.2) — the board wraps, so the only thing to crash into is yourself.
 */
export class WormScene extends Phaser.Scene {
  private lang = 'de';
  private startRung = 1;
  private rung = 1;
  private wordsDone = 0;
  private books = WORM.startBooks;

  private classes: LetterClass[] = [];
  private board: WormBoard | null = null;
  private worm!: Worm;
  /** Where the finger last asked the worm to go, or null when the keys are driving (§5.1). */
  private steerAt: Pt | null = null;
  /** Keyboard only: a heading to turn toward, held until another key is pressed. */
  private steerTo: number | null = null;
  /**
   * The pointer that is actually down — the object itself, not `activePointer` (§17.15).
   *
   * A PC with a touch screen has both a mouse pointer and a touch pointer, and `activePointer` is
   * whichever Phaser saw last. Holding the reference the `pointerdown` handed us means the drag is
   * read from the finger that started it and can never be answered by a mouse sitting still in a
   * corner of the screen.
   */
  private dragPointer: Phaser.Input.Pointer | null = null;
  /**
   * Where the pointer was when a bite released the target (§17.11).
   *
   * A held finger produces no move events, so the per-frame re-aim would put the target straight
   * back on the letter that has just been eaten and the worm would orbit the empty spot again. It
   * stays suppressed until the finger actually goes somewhere.
   */
  private releasedAt: Pt | null = null;
  /** How far the head was from its target last frame — how an overshoot is spotted (§5.1.3). */
  private aimDist = Infinity;
  /**
   * Is a knot currently possible? Off the board it is not (§5.5), and it stays impossible after
   * coming back until the worm is seen to be clear of itself — otherwise a worm that tangled where
   * nobody could see it dies on the frame it becomes visible again, which is the same unfairness
   * with an audience.
   */
  private knotArmed = true;
  /**
   * How many knots this run has tied — dev diagnostics only, and the counter §5.5.1 is measured by.
   *
   * A knot and a wrong bite both cost a book, so books alone cannot tell "one knot" from "a knot
   * that repeated itself". `tools/test-worm.mjs` reads this.
   */
  private knots = 0;
  /**
   * Where the finger was when a knot sent the worm back to the neutral position (§5.5.1), or null
   * when nothing is being ignored.
   *
   * While this is set, steering is ignored until the finger actually goes somewhere — `STEER_GATE`
   * pixels away, or up and down again. `releasedAt` is not enough for this: it is cleared by the
   * very next `pointermove`, and a finger resting on a screen sends those from its own jitter. The
   * worm would be aimed straight back at the spot it just died on, by a child who has not asked for
   * anything.
   */
  private steerGateFrom: Pt | null = null;
  /** Dev diagnostics only: how many of each input event this scene has actually been handed. */
  private evSeen: Record<string, number> = {};
  private usedWordIds = new Set<string>();
  /** Every word finished this run, for the end card (§10.2). */
  private eatenWords: { id: string; text: string }[] = [];
  /**
   * Every letter the worm is carrying, oldest first — the WHOLE run, not just this word (§5.6).
   *
   * A finished word is not cashed in and cleared: the next word's letters connect straight onto the
   * back of it, so one run is one worm that grows from four segments to everything it has read.
   */
  private carried: string[] = [];
  /** How long the worm was when this word started — what a wrong bite falls back to (§5.4). */
  private beadsAtWordStart = WORM.startBeads;
  /** Where one finished word ends and the next begins, as indices into `carried` (§5.6). */
  private wordBreaks: number[] = [];

  private running = false;
  private over = false;
  /**
   * Scene time until which the worm stands still on a fresh board (§5.1.4).
   *
   * Steering still registers while it waits, so it sets off the way the child asked the moment it
   * moves — the pause is thinking time, not a dead input.
   */
  private holdUntil = 0;
  private speed = WORM.speed[0];
  private wormGfx!: Phaser.GameObjects.Graphics;
  /** Drawn on the edge the worm went out by, while it is off the board (§5.2). */
  private edgeMark!: Phaser.GameObjects.Graphics;
  private beadLabels: Phaser.GameObjects.Text[] = [];
  private tileArt = new Map<number, { disc: Phaser.GameObjects.Arc; label: Phaser.GameObjects.Text }>();
  private picture: Phaser.GameObjects.Image | null = null;
  private slotText!: Phaser.GameObjects.Text;
  private bookIcons: Phaser.GameObjects.Text[] = [];
  private rungText!: Phaser.GameObjects.Text;
  /** Rung 3 only: the word is shown until the first bite, then it fades (§4.3). */
  private wordShown = false;
  /** The spoken instruction is said once per run, on the first board (§9). */
  private introSpoken = false;
  /** The straightening (§5.6): where each bead is tweening to, and how far along it is. */
  private straighten: { to: Pt[]; from: Pt[]; t: number } | null = null;

  constructor() {
    super({ key: 'Worm' });
  }

  init(data: WormInit): void {
    this.lang = data.lang;
    this.startRung = Math.min(Math.max(1, Math.floor(data.startRung ?? 1)), WORM.words);
    this.rung = this.startRung;
    this.wordsDone = 0;
    this.books = WORM.startBooks;
    this.board = null;
    this.steerAt = null;
    this.steerTo = null;
    this.dragPointer = null;
    this.releasedAt = null;
    this.aimDist = Infinity;
    this.knotArmed = true;
    this.knots = 0;
    this.steerGateFrom = null;
    this.usedWordIds = new Set();
    this.eatenWords = [];
    this.carried = [];
    this.beadsAtWordStart = WORM.startBeads;
    this.wordBreaks = [];
    this.running = false;
    this.over = false;
    this.holdUntil = 0;
    this.speed = WORM.speed[0];
    this.tileArt = new Map();
    this.beadLabels = [];
    this.bookIcons = [];
    this.picture = null;
    this.wordShown = false;
    this.introSpoken = false;
    this.straighten = null;
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    this.drawSky();
    this.input.setDefaultCursor('pointer');

    const pack = getLettersPack(this.lang) ?? getLettersPack('en');
    this.classes = pack ? classesOf(pack) : [];

    this.wormGfx = this.add.graphics().setDepth(30);
    // Above the worm, below the HUD: it has to be readable against a worm sitting right behind it.
    this.edgeMark = this.add.graphics().setDepth(45);
    this.drawHud();
    addMuteToggle(this, { depth: 50 });
    this.bindInput();

    // The worm starts mid-board heading right, and is repositioned exactly once afterwards: when a
    // KNOT took the word (§5.5). Not between words, and not after a wrong bite (§5.4).
    this.spawnWorm(WORM.startBeads);

    // Dev/staging only (`?worm`): a handle on the run's state, so a driver script can read what is
    // on the board instead of guessing at pixels. Never present on production — devFlag is a
    // hostname check (src/game/dev.ts).
    if (devFlag('worm')) {
      (window as unknown as Record<string, unknown>).__worm = (): unknown => ({
        rung: this.rung, books: this.books, wordsDone: this.wordsDone, running: this.running,
        knots: this.knots,
        over: this.over, word: this.board?.word.text ?? null, eaten: this.board?.eaten ?? 0,
        next: this.board ? nextGlyph(this.board) : null,
        carried: this.carried.join(''),
        // Input diagnostics (dev only): which pointer is driving, and how many events have been
        // seen. A touch screen that answers taps but not drags is a question about THESE numbers.
        input: {
          drag: this.dragPointer
            ? { id: this.dragPointer.id, down: this.dragPointer.isDown, wasTouch: this.dragPointer.wasTouch,
                x: Math.round(this.dragPointer.worldX), y: Math.round(this.dragPointer.worldY) }
            : null,
          seen: { ...this.evSeen },
          aim: this.steerAt ? { x: Math.round(this.steerAt.x), y: Math.round(this.steerAt.y) } : null,
        },
        length: this.worm.beads,
        head: { x: Math.round(this.worm.head.x), y: Math.round(this.worm.head.y) },
        angle: Number(this.worm.angle.toFixed(3)),
        tiles: (this.board?.tiles ?? []).map((t) => ({
          glyph: t.glyph, target: t.target, x: Math.round(t.x), y: Math.round(t.y),
        })),
      });
    }

    this.showHowTo(() => this.dealRung());
  }

  // ── the sky ──────────────────────────────────────────────────────────────────

  /**
   * Near-black with small, still stars (§4.1).
   *
   * This is a contrast requirement rather than a theme: a lettered bead has to stay legible at
   * 44px while moving, against a ground carrying twenty other lettered tiles. The player's chosen
   * backdrop is ignored here for exactly that reason — every one of them is a photograph.
   *
   * The stars never move (a parallax field makes the worm's own speed unreadable) and are kept off
   * the tile grid's lines, because a 2px dot beside an `i` is a second `i`.
   */
  private drawSky(): void {
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, SKY).setDepth(-20);
    const g = this.add.graphics().setDepth(-19);
    const rng = Math.random;
    for (let i = 0; i < 120; i++) {
      g.fillStyle(0xffffff, 0.18 + rng() * 0.17)
        .fillCircle(rng() * GAME_WIDTH, rng() * GAME_HEIGHT, rng() < 0.2 ? 1.6 : 1);
    }
  }

  // ── input ────────────────────────────────────────────────────────────────────

  /**
   * The finger is a destination, not a gesture (§5.1) — the Wagon's model, for the reason recorded
   * there: a five-year-old has a point, not a swipe vocabulary. Arrows and WASD for the desktop
   * build and for testing.
   */
  private bindInput(): void {
    const bump = (n: string): void => { this.evSeen[n] = (this.evSeen[n] ?? 0) + 1; };
    const aim = (p: Phaser.Input.Pointer): void => {
      if (this.gated(p.worldX, p.worldY)) return;
      bump('aim');
      this.steerTo = null;
      this.aimDist = Infinity;
      this.releasedAt = null;
      this.steerAt = this.aimPoint(p.worldX, p.worldY);
    };
    // A fresh press is always a deliberate instruction — it opens the gate whatever it was.
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      bump('down');
      this.steerGateFrom = null;
      this.dragPointer = p;
      aim(p);
    });
    // A move steers whenever it comes from the pointer that is driving — `isDown` is not trusted on
    // its own, because a touch screen that reports it oddly is exactly the device this has to work
    // on. A move from a pointer that IS down adopts it as the driver, so a finger can take over
    // from a mouse mid-board without a fresh press.
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      bump(p.isDown ? 'move-down' : 'move-up');
      if (p.isDown) this.dragPointer = p;
      if (p === this.dragPointer) aim(p);
    });
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (!this.dragPointer || p === this.dragPointer) { this.dragPointer = null; this.releasedAt = null; }
    });
    // A finger dragged off the canvas never sends an up: let go of it rather than steering at the
    // last place it was seen forever.
    this.input.on('gameout', () => { this.dragPointer = null; this.releasedAt = null; });

    // The keys ask for a heading rather than a point, and the worm still turns at its own rate —
    // so even on a keyboard it curves rather than snapping to the compass.
    // Nothing else: the per-frame re-read of §5.1.2 lives in update(), because a finger that is
    // held still fires no move events at all.
    this.input.keyboard?.on('keydown', (e: KeyboardEvent) => {
      const map: Record<string, number> = {
        ArrowUp: -Math.PI / 2, ArrowDown: Math.PI / 2, ArrowLeft: Math.PI, ArrowRight: 0,
        w: -Math.PI / 2, s: Math.PI / 2, a: Math.PI, d: 0,
      };
      const want = map[e.key];
      if (want !== undefined) { this.steerAt = null; this.steerTo = want; }
    });
  }

  /**
   * A pointer position as a steering target: **clamped** into the play area, never wrapped into it.
   * A tap on the HUD band at the top of the screen means "come up here"; wrapping it would send the
   * worm to the bottom instead (§17.13).
   */
  private aimPoint(x: number, y: number): Pt {
    return {
      x: Phaser.Math.Clamp(x, WORM.play.x, WORM.play.x + WORM.play.w),
      y: Phaser.Math.Clamp(y, WORM.play.y, WORM.play.y + WORM.play.h),
    };
  }

  /**
   * The neutral position: mid-board, heading right, body laid out straight behind it.
   *
   * Where the run starts, and — since §5.5.1 — where a knot puts the worm back. Straight, with the
   * whole width of the board in front of it and nothing of itself anywhere near its head.
   */
  private spawnWorm(beads: number): void {
    this.worm = newWorm(
      { x: WORM.play.x + 200, y: WORM.play.y + WORM.play.h / 2 }, 0,
      beads, WORM.beadSpacing, WORM.play,
    );
  }

  /**
   * Let go of the steering, so the worm leaves a respawn going straight (§5.5.1).
   *
   * A child who has just tied a knot still has their finger planted exactly where they tied it. The
   * per-frame re-aim of `followPointer` would read it on the first live frame and turn the worm
   * hard back toward the place it just died — which, on a long worm at the tightest turn radius, is
   * another knot within half a circle. Suppressed through `releasedAt`, the same way §17.11
   * suppresses a target a bite has just eaten: it holds until the finger actually goes somewhere.
   */
  private releaseSteering(): void {
    this.steerAt = null;
    this.steerTo = null;
    this.aimDist = Infinity;
    this.releasedAt = null;
    const p = this.dragPointer;
    this.steerGateFrom = p && p.isDown ? { x: p.worldX, y: p.worldY } : null;
  }

  /**
   * Is this pointer position still the finger that was on the knot (§5.5.1)?
   *
   * The gate opens the moment the finger goes somewhere — `STEER_GATE` pixels is well above the
   * jitter a resting finger sends and well below a deliberate point — or on a fresh press. It never
   * closes on its own, so a child who does nothing gets a worm going straight, not one turning back
   * into the place it just died.
   */
  private gated(x: number, y: number): boolean {
    const from = this.steerGateFrom;
    if (!from) return false;
    if (Math.hypot(x - from.x, y - from.y) < STEER_GATE) return true;
    this.steerGateFrom = null;
    return false;
  }

  /**
   * While a finger or a button is down, re-read where it is **every frame** (§5.1.2).
   *
   * Move events are not enough on a touch screen. A finger planted on the board and held still
   * fires none at all — which is exactly how a child holds a screen, and it made the mode feel like
   * it only answered short taps while a mouse (which is never quite still) worked perfectly.
   *
   * The one exception is a target a bite has just released (§17.11): re-aiming would put it
   * straight back on the letter that is no longer there and the worm would orbit the empty spot.
   * That stays suppressed until the finger actually goes somewhere.
   */
  private followPointer(): void {
    const p = this.dragPointer;
    if (!p) return;
    if (!p.isDown) { this.dragPointer = null; return; }
    if (this.gated(p.worldX, p.worldY)) return;
    const at = this.aimPoint(p.worldX, p.worldY);
    if (this.releasedAt && Math.hypot(at.x - this.releasedAt.x, at.y - this.releasedAt.y) < 14) return;
    this.releasedAt = null;
    this.steerTo = null;
    this.steerAt = at;
  }

  // ── the run ──────────────────────────────────────────────────────────────────

  private dealRung(): void {
    const progress = letterProgress(this.lang);
    const ranked = this.classes.map((c) => c.id);
    const board = dealWormBoard({
      rung: this.rung,
      classes: this.classes,
      metIds: metClassIds(progress, ranked),
      knownIds: new Set(ranked.filter((id) => isKnown(progress, id))),
      lang: this.lang,
      picturePool: getPictureWordsPool(this.lang),
      usedWordIds: this.usedWordIds,
      worm: this.worm,
      bounds: WORM.play,
      rng: Math.random,
    });
    // The picture pool has nothing left to offer: end kindly rather than stand on an empty board.
    if (!board) { this.endRun(); return; }
    this.usedWordIds.add(board.word.id);
    this.board = board;
    this.speed = WORM.speed[Math.min(this.rung, WORM.speed.length) - 1];

    const key = wordIconKey(board.word.id);
    if (!this.textures.exists(key)) {
      this.load.image(key, wordIconUrl(board.word.id));
      this.load.once(Phaser.Loader.Events.COMPLETE, () => this.layoutBoard());
      this.load.start();
      return;
    }
    this.layoutBoard();
  }

  private layoutBoard(): void {
    const board = this.board;
    if (!board) return;
    this.clearBoardArt();
    this.wordShown = true;

    this.picture = this.add.image(GAME_WIDTH / 2, 92, wordIconKey(board.word.id))
      .setDepth(40).setAlpha(0);
    this.picture.setDisplaySize(112, 112);
    this.paintSlots();
    this.paintBooks();
    this.rungText.setText(`${Math.min(this.wordsDone + 1, WORM.words)} / ${WORM.words}`);

    // Letters arrive the way a star appears (§5.4.1) — scaled up from nothing, staggered across the
    // board, so the word assembles instead of snapping on.
    const font = scriptFontFamily(this.lang);
    board.tiles.forEach((t, i) => {
      const disc = this.add.circle(t.x, t.y, WORM.tileRadius, TILE_FILL)
        .setStrokeStyle(3, TILE_RING).setDepth(20);
      const label = this.add.text(t.x, t.y, t.glyph, {
        fontFamily: font, fontSize: '40px', color: '#ffffff',
      }).setOrigin(0.5).setDepth(21);
      disc.setScale(0).setAlpha(0);
      label.setScale(0).setAlpha(0);
      this.tweens.add({
        targets: [disc, label], scale: 1, alpha: 1,
        duration: WORM.materializeMs, delay: i * WORM.materializeStaggerMs, ease: 'Back.easeOut',
      });
      this.tileArt.set(t.id, { disc, label });
    });
    this.tweens.add({ targets: this.picture, alpha: 1, duration: WORM.materializeMs });

    this.markNext();
    // Stand still for a moment: the picture has to be read, the letters are still arriving, and a
    // worm already crossing the board is a worm the child is chasing rather than driving (§5.1.4).
    this.holdUntil = this.time.now + WORM.startHoldMs;
    this.running = true;

    // Said once, on the first board of a run: the mode's whole instruction, out loud, for a child
    // who has just tapped past a card they may not have read. Not repeated per word — by the second
    // picture they know, and a game that keeps explaining itself is a game nobody listens to.
    if (!this.introSpoken) {
      this.introSpoken = true;
      speakRulePrompt(i18next.t('worm.spokenIntro'), this.lang);
    }
  }

  /** The tile the word wants next wears a breathing cyan ring, on the rungs that show the word. */
  private markNext(): void {
    const board = this.board;
    if (!board) return;
    const want = nextGlyph(board);
    for (const t of board.tiles) {
      const art = this.tileArt.get(t.id);
      if (!art) continue;
      const isNext = this.showsWord() && t.target && t.glyph === want;
      art.disc.setStrokeStyle(3, isNext ? NEXT_RING : TILE_RING);
    }
  }

  /** Does this rung write the word out under the picture (§4.3)? */
  private showsWord(): boolean {
    if (this.rung <= WORM.showWordRungs) return true;
    return this.rung === WORM.fadeWordRung && this.wordShown;
  }

  private paintSlots(): void {
    const board = this.board;
    if (!board) { this.slotText.setText(''); return; }
    const done = spelledSoFar(board);
    // The word, or one slot per step — either way the letters already eaten are readable behind
    // you, which is how a child checks their own work.
    this.slotText.setText(this.showsWord() ? board.word.text : `${done}${'·'.repeat(Math.max(0, board.word.steps.length - board.eaten))}`);
  }

  private paintBooks(): void {
    this.bookIcons.forEach((b, i) => b.setAlpha(i < this.books ? 1 : 0.22));
  }

  // ── the loop ─────────────────────────────────────────────────────────────────

  /**
   * One frame, not one tick: the worm moves continuously and turns at its own rate, so there is no
   * step to quantise anything to (§5.1). `delta` is capped — a tab that was in the background for
   * two seconds must not teleport the worm through half the board and into its own tail.
   */
  update(_time: number, delta: number): void {
    // Waiting on a fresh board: input is read, nothing moves, nothing can be hit (§5.1.4).
    const holding = this.running && this.time.now < this.holdUntil;
    if (holding) this.followPointer();
    if (this.running && !holding) {
      const dt = Math.min(delta, 50) / 1000;
      this.followPointer();
      // A fixed tightest-circle RADIUS, so the rate rises with the rung's speed and the worm
      // handles the same at 210px/s and at 310 (§5.1). Off the board it turns harder, because out
      // there it is not playing, it is coming back (§5.2).
      const off = !inside(this.worm.head, WORM.play);
      const rate = (this.speed / WORM.turnRadius) * (off ? WORM.outsideTurnBoost : 1);
      // The leash: past the margin with nowhere to be, it sends itself back to the board — along a
      // heading picked to keep clear of its own body, because a hard turn home with the tail in the
      // way is a knot nobody saw (§5.2.2).
      const leashed = off && !this.steerAt && this.steerTo === null
        && outsideBy(this.worm.head, WORM.play) > WORM.outsideMargin;
      if (this.steerAt) steer(this.worm, this.steerAt, rate, dt, WORM.play);
      else if (leashed) {
        // A rate of its own, from its own length: the circle the leash carves is always longer than
        // the worm, so the turn home cannot run the head into its own tail (§5.2.2).
        const home = safeReturnAngle(
          this.worm, WORM.play, WORM.headRadius * 2, WORM.beadSpacing,
          WORM.dangerPx * 2, WORM.neckBeads, WORM.hitRadius,
        );
        const homeRate = leashTurnRate(
          this.worm, this.speed, WORM.turnRadius, WORM.beadSpacing, WORM.hitRadius,
        );
        steerAngle(this.worm, home, homeRate, dt);
      } else if (this.steerTo !== null) steerAngle(this.worm, this.steerTo, rate, dt);
      advance(this.worm, this.speed, dt, WORM.beadSpacing, WORM.play);
      this.checkArrival();
      this.checkBite();
      // **A knot only counts on the board, and only once the worm is clear of itself** (§5.5).
      // Off the board the child cannot see the worm, cannot see the warning pulse, and is often not
      // the one steering — the leash is. A run must never end on something nobody watched happen,
      // and it must not end on the frame the worm becomes visible again either.
      const tangled = selfHit(this.worm, WORM.beadSpacing, WORM.play, WORM.neckBeads, WORM.hitRadius);
      if (off) this.knotArmed = false;
      else if (!tangled) this.knotArmed = true;
      if (this.running && !off && this.knotArmed && tangled) this.loseWord('knot');
    }
    this.paintWorm(holding);
    this.paintEdgeMark();
  }

  /**
   * Where the worm is when it is not on the screen (§5.2).
   *
   * A chevron on the edge it went out by, in the head's own colour, sitting exactly where it will
   * come back through if it comes back on its current heading. Without it a worm that slips off the
   * bottom is simply gone, and the child has no idea which way to point.
   */
  private paintEdgeMark(): void {
    const g = this.edgeMark;
    g.clear();
    if (this.over || this.straighten) return;
    const head = this.worm.head;
    if (inside(head, WORM.play)) return;

    // Inset by more than the marker's own reach, so the arrow and its ring are drawn ON the board
    // and never half off the edge they are pointing at.
    const at = nearestInside(head, WORM.play, 42);
    const away = Math.atan2(head.y - at.y, head.x - at.x);
    const far = Math.min(1, outsideBy(head, WORM.play) / (WORM.outsideMargin * 1.6));
    // A triangle pointing out at the worm, and a ring that tightens as it gets further away.
    const r = 17;
    const pts = [0, 2.5, -2.5].map((o) => ({
      x: at.x + Math.cos(away + o) * (o === 0 ? r * 1.5 : r),
      y: at.y + Math.sin(away + o) * (o === 0 ? r * 1.5 : r),
    }));
    g.fillStyle(HEAD, 0.55 + 0.45 * far);
    g.fillTriangle(pts[0].x, pts[0].y, pts[1].x, pts[1].y, pts[2].x, pts[2].y);
    g.lineStyle(3, HEAD, 0.35 + 0.35 * far);
    g.strokeCircle(at.x, at.y, r + 10 - far * 5);
  }

  /**
   * Arrived where the finger asked for? Then let go of it and fly on (§5.1.3).
   *
   * Without this the worm orbits the destination for as long as the child holds still: it arrives,
   * overshoots by its own turning radius, comes round and overshoots again. "Go there" has to end
   * when it gets there.
   *
   * Letting go is not enough on its own, because the held finger is re-read every frame (§5.1.2) —
   * so the point is remembered as `releasedAt` and re-aiming waits until the finger has actually
   * moved, which is the same mechanism a bite under a parked finger uses (§17.11).
   */
  private checkArrival(): void {
    const target = this.steerAt;
    if (!target) return;
    const there = reachedTarget(
      this.worm.head, target, this.aimDist, WORM.arriveRadius, WORM.arriveNearPx,
    );
    this.aimDist = directDist(this.worm.head, target);
    if (!there) return;
    this.releasedAt = target;
    this.steerAt = null;
    this.aimDist = Infinity;
  }

  /** Touching a letter is eating it — there is no aiming, and no hovering (§5.4). */
  private checkBite(): void {
    const board = this.board;
    if (!board) return;
    const tile = tileAt(board, this.worm.head, WORM.biteRadius, WORM.play);
    if (!tile) return;
    // The letter the finger was pointing at is gone, so the target goes with it: hold a finger on a
    // letter and the worm would otherwise keep circling the empty spot it used to be in. Released,
    // it flies straight on along its current heading until the child points somewhere else — which
    // is what "I got that one" should feel like. A target somewhere ELSE is left alone, so pointing
    // at the next letter while the head takes this one still works.
    if (this.steerAt && dist(this.steerAt, tile, WORM.play) < WORM.biteRadius + WORM.tileRadius) {
      this.releasedAt = this.steerAt;
      this.steerAt = null;
      this.aimDist = Infinity;
    }
    if (bite(board, tile) === 'right') {
      this.worm.beads += 1;
      this.carried.push(tile.glyph);
      this.eatTile(tile.id);
      playChompSound();
      this.wordShown = false;
      this.paintSlots();
      this.markNext();
      if (isComplete(board)) this.finishWord();
    } else {
      // The whole board is about to dissolve; nothing on it is worth steering at any more.
      this.releasedAt = this.steerAt;
      this.steerAt = null;
      this.loseWord();
    }
  }

  private eatTile(id: number): void {
    const art = this.tileArt.get(id);
    if (!art) return;
    this.tileArt.delete(id);
    this.tweens.add({
      targets: [art.disc, art.label], scale: 0, alpha: 0, duration: 160,
      onComplete: () => { art.disc.destroy(); art.label.destroy(); },
    });
  }

  // ── the two ways a word ends ─────────────────────────────────────────────────

  /**
   * The straightening (§5.6): the worm unwinds into a line and the word is written across the
   * screen in coloured beads, tail at the left, head at the right — the head is the pen, so the
   * word reads the way it was eaten.
   */
  private finishWord(): void {
    const board = this.board;
    if (!board) return;
    this.running = false;
    this.wordsDone += 1;
    this.eatenWords.push({ id: board.word.id, text: board.word.text });
    playGateSound();
    speakRulePrompt(board.word.text, this.lang);

    // The worm unwinds into a straight line, tail at the LEFT and head at the right, so the beads
    // read as the word in the order they were eaten. This is the mode's signature moment.
    const n = this.worm.beads;
    // A small gap at each word boundary, so a chain of five words reads as five words rather than
    // as one long letter soup — the one place this mode has to look like reading.
    const gap = 14;
    const breaks = [...this.wordBreaks, this.carried.length];
    const extraAt = (fromTail: number): number => {
      const letter = fromTail - WORM.startBeads;
      return gap * breaks.filter((b) => b <= letter).length;
    };
    const totalExtra = extraAt(n - 1);
    // Opened out so the letters can be read — but never wider than the screen, because by the fifth
    // word the worm is the whole run and there are twenty-odd segments to lay out.
    const spacing = Math.min(
      WORM.beadSpacing + 16,
      (GAME_WIDTH - 90 - totalExtra) / Math.max(1, n - 1),
    );
    const from = beadPositions(this.worm, WORM.beadSpacing, WORM.play);
    const to = from.map((_, i) => {
      const fromTail = n - 1 - i;
      return {
        x: GAME_WIDTH / 2 + (fromTail - (n - 1) / 2) * spacing + extraAt(fromTail) - totalExtra / 2,
        y: GAME_HEIGHT / 2 - 40,
      };
    });
    this.straighten = { from, to, t: 0 };
    this.tweens.add({
      targets: this.straighten, t: 1, duration: 520, ease: 'Cubic.easeInOut',
    });

    const banner = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 110, i18next.t('worm.wordDone', { word: board.word.text }), {
      fontFamily: scriptFontFamily(this.lang), fontSize: '64px', color: '#ffd23f',
      align: 'center', padding: { x: 30, y: 16 },
    }).setOrigin(0.5).setDepth(120).setAlpha(0);
    const bg = addRoundedLabelBg(this, banner, { radius: 22 });
    bg.setAlpha(0);
    this.tweens.add({ targets: [banner, bg], alpha: 1, duration: 260 });

    this.time.delayedCall(WORM.straightenMs, () => {
      banner.destroy();
      bg.destroy();
      this.straighten = null;
      // The worm KEEPS everything it has eaten (§5.6): only the board dissolves, and the next
      // word's letters connect onto the back of what is already there.
      this.beadsAtWordStart = this.worm.beads;
      this.wordBreaks.push(this.carried.length);
      this.dissolve(this.worm.beads, false, () => {
        if (this.wordsDone >= WORM.words) { this.endRun(); return; }
        this.rung = Math.min(WORM.words, this.rung + 1);
        this.dealRung();
      });
    });
  }

  /**
   * A wrong bite (§5.4): one book, and the word.
   *
   * The tile is NOT left behind — taking the whole word away is what keeps a board from becoming
   * unspellable, and it is what makes every bite a decision. The rung does not advance: a word lost
   * is re-dealt at the same rung with a different picture.
   */
  /**
   * A word lost — by misreading a letter (§5.4) or by tying a knot (§5.5). One book, and the word.
   *
   * Both cost the same because both are mistakes; what differs is only how it is announced. A knot
   * gets a word for what just happened, because a child who drove into their own tail deserves to
   * be told that is what it was.
   */
  private loseWord(reason: 'bite' | 'knot' = 'bite'): void {
    if (!this.board) return;
    this.running = false;
    if (reason === 'knot') this.knots += 1;
    this.books -= 1;
    this.paintBooks();
    // One sound, and nothing said. The child is still steering; a spoken correction lands over a
    // board that has already changed, and by the time it finishes they are reading a new word (§6.3).
    playThudSound();
    this.cameras.main.flash(WORM.wrongFlashMs, 255, 255, 255, false);

    let banner: Phaser.GameObjects.Text | null = null;
    let bannerBg: Phaser.GameObjects.NineSlice | null = null;
    if (reason === 'knot') {
      banner = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, i18next.t('worm.knot'), {
        fontFamily: FONT_STACK, fontSize: '54px', color: '#ffd166', align: 'center',
        padding: { x: 30, y: 18 },
      }).setOrigin(0.5).setDepth(120).setAlpha(0);
      const bg = addRoundedLabelBg(this, banner, { radius: 22 });
      bg.setAlpha(0);
      bannerBg = bg;
      this.tweens.add({ targets: [banner, bg], alpha: 1, duration: 200 });
    }

    const wait = (reason === 'knot' ? WORM.knotHoldMs : WORM.wrongWordMs) - WORM.materializeMs;
    this.time.delayedCall(wait, () => {
      banner?.destroy();
      bannerBg?.destroy();
      // Back to the length it was when this word started: the run keeps every FINISHED word and
      // gives back only the half-eaten one (§5.4).
      //
      // A KNOT also goes back to the neutral position (§5.5.1), and it has to: the worm carries
      // its length across words (§5.6), so a knot on a fresh word asks to go back to the length it
      // already is — and the `beads !== keepBeads` guard below then skipped the straightening
      // entirely. The tangled body went into the next board intact and the first live frame was
      // another knot. One knot took every remaining book, three or four boards in a row, and none
      // of them were mistakes the child got to make.
      this.dissolve(this.beadsAtWordStart, reason === 'knot', () => {
        if (this.books <= 0) { this.endRun(); return; }
        this.dealRung();
      });
    });
  }

  /**
   * Everything on the board lifts off and fades upward (§5.4.1), and the worm gives back its beads
   * where it stands — same head cell, same heading, three beads again.
   *
   * `respawn` moves it back to the neutral position instead, which only a knot asks for (§5.5.1).
   */
  private dissolve(keepBeads: number, respawn: boolean, then: () => void): void {
    const art = [...this.tileArt.values()];
    this.tileArt.clear();
    const targets: Phaser.GameObjects.GameObject[] = art.flatMap((a) => [a.disc, a.label]);
    if (this.picture) targets.push(this.picture);
    const dying = this.picture;
    this.picture = null;
    if (targets.length > 0) {
      this.tweens.add({
        targets, alpha: 0, y: '-=26', duration: WORM.materializeMs, ease: 'Sine.easeIn',
        onComplete: () => {
          art.forEach((a) => { a.disc.destroy(); a.label.destroy(); });
          dying?.destroy();
        },
      });
    }
    // Letters past `keepBeads` were the half-eaten word's; they go with it.
    this.carried.length = Math.max(0, keepBeads - WORM.startBeads);
    this.wordBreaks = this.wordBreaks.filter((b) => b <= this.carried.length);
    if (respawn) {
      // Not `shrinkTo`, for two reasons. It is skipped altogether when the length has not changed,
      // which is exactly the knot that spirals (§5.5.1); and even when it does run it keeps the
      // head where it is, pointing into the curl it just died in.
      this.spawnWorm(keepBeads);
      this.releaseSteering();
      // And the new board cannot open on a knot either — `update` re-arms this only once the worm
      // has been seen clear of itself, which a worm standing straight at the spawn is.
      this.knotArmed = false;
    } else if (this.worm.beads !== keepBeads) {
      shrinkTo(this.worm, keepBeads, WORM.beadSpacing, WORM.play);
    }
    this.board = null;
    this.slotText.setText('');
    this.time.delayedCall(WORM.materializeMs, then);
  }

  // ── the endings ──────────────────────────────────────────────────────────────

  /**
   * One card for both endings (§10.2) — a run ends with what was READ, not with whether the
   * worm survived. No cross, no red, no count of mistakes, and it leaves by itself after four
   * seconds so nobody is stranded on it.
   */
  private endRun(): void {
    if (this.over) return;
    this.over = true;
    this.running = false;
    this.wormGfx.clear();
    this.beadLabels.forEach((t) => t.destroy());
    this.beadLabels = [];
    if (this.wordsDone > 0) playJourneyFanfare();

    // What was READ, never what was missed. A run of nothing gets an invitation rather than a zero.
    const headline = this.wordsDone >= WORM.words
      ? i18next.t('worm.runDone')
      : this.wordsDone === 0
        ? i18next.t('worm.ateNone')
        : i18next.t('worm.ate', { count: this.wordsDone });
    const msg = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 60, `🪱\n${headline}`, {
      fontFamily: scriptFontFamily(this.lang), fontSize: '54px', color: '#7fe7ff',
      align: 'center', padding: { x: 34, y: 20 }, lineSpacing: 10,
    }).setOrigin(0.5).setDepth(120).setAlpha(0).setScale(0.85);
    const bg = addRoundedLabelBg(this, msg, { radius: 24 });
    bg.setAlpha(0).setScale(0.85);
    this.tweens.add({ targets: [msg, bg], alpha: 1, scale: 1, duration: 320, ease: 'Back.easeOut' });

    // The words that were read, with their pictures. The ones that were lost are not listed.
    const shown = this.eatenWords.slice(-WORM.words);
    const spacing = 210;
    shown.forEach((w, i) => {
      const x = GAME_WIDTH / 2 + (i - (shown.length - 1) / 2) * spacing;
      const y = GAME_HEIGHT / 2 + 110;
      if (this.textures.exists(wordIconKey(w.id))) {
        this.add.image(x, y, wordIconKey(w.id)).setDisplaySize(96, 96).setDepth(120);
      }
      this.add.text(x, y + 74, w.text, {
        fontFamily: scriptFontFamily(this.lang), fontSize: '30px', color: '#ffd23f',
      }).setOrigin(0.5).setDepth(120);
    });

    const shownAt = performance.now();
    const leave = (): void => {
      if (performance.now() - shownAt < 900) return;
      this.input.off('pointerdown', leave);
      this.scene.start('Menu');
    };
    this.input.on('pointerdown', leave);

    // The web-only buy card, and the rule `recipe-mode.md` paid for: **never on a card that is
    // about to leave by itself**. This card is the one screen that shows what the run actually
    // read; a promo that slides over it at two seconds and is then destroyed by the scene change
    // at four is noise charged to the only moment the child had to look at their own words.
    //
    // So it is one or the other. With a card due, the screen waits for a tap instead of timing out
    // — nobody is stranded, because the card carries its own dismiss and a tap anywhere else
    // returns to the menu.
    if (shouldShowBuyPrompt()) maybeShowBuyPrompt(this, { delayMs: 3200 });
    else this.time.delayedCall(4000, leave);
  }

  // ── painting ─────────────────────────────────────────────────────────────────

  /**
   * The worm: **overlapping circles along the path the head took**, drawn tail-first so the head
   * sits on top.
   *
   * `beadSpacing` is well under `2 * beadRadius`, so consecutive segments overlap and the body
   * reads as one animal rather than as a row of separate dots — while still being visibly
   * segmented, which is what makes a lettered segment a *segment* and not a sticker.
   */
  private paintWorm(holding = false): void {
    if (this.over) return;
    const g = this.wormGfx;
    g.clear();
    const board = this.board;
    const beads = this.straighten
      ? this.straighten.from.map((p, i) => this.lerpPoint(p, this.straighten!.to[i], this.straighten!.t))
      : beadPositions(this.worm, WORM.beadSpacing, WORM.play);
    const danger = board && !this.straighten
      ? dangerAhead(this.worm, WORM.beadSpacing, WORM.play, WORM.neckBeads, WORM.dangerPx, WORM.hitRadius)
      : null;

    // Segments carry the RUN: the one behind the head holds the letter eaten last, so everything
    // read so far spells out tail → head and the head itself stays a face rather than a letter with
    // eyes on it.
    const eaten = this.carried.length;
    const steps = this.carried;
    while (this.beadLabels.length < eaten) {
      this.beadLabels.push(this.add.text(0, 0, '', {
        fontFamily: scriptFontFamily(this.lang), fontSize: '28px', color: '#1a1230', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(31));
    }
    this.beadLabels.forEach((l, i) => l.setVisible(i < eaten));

    for (let i = beads.length - 1; i >= 0; i--) {
      const p = beads[i];
      const isHead = i === 0;
      // Letter k of the word rides segment k+1, counted back from the head.
      const letterIndex = eaten - i;
      const carries = !isHead && letterIndex >= 0 && letterIndex < eaten;
      const threatened = danger !== null && dist(p, danger, WORM.play) < 1;
      const radius = isHead ? WORM.headRadius : WORM.beadRadius;
      const colour = isHead ? HEAD : (carries ? RAMP[letterIndex % RAMP.length] : BODY_BASE);
      g.fillStyle(threatened ? DANGER : colour, 1).fillCircle(p.x, p.y, radius);
      g.lineStyle(2, 0x0b0d1c, 0.85).strokeCircle(p.x, p.y, radius);
      if (isHead && holding) {
        // Waiting to go: a ring that breathes out from the head, so a still worm reads as ready
        // rather than as a game that has stopped (§5.1.4).
        const t = (this.time.now % 600) / 600;
        g.lineStyle(3, HEAD, 0.5 * (1 - t));
        g.strokeCircle(p.x, p.y, radius + 4 + t * 16);
      }
      if (isHead) {
        // Two eyes, forward of centre and either side of the heading — the whole difference
        // between a worm and a row of dots. Perpendicular, never diagonal.
        const a = this.straighten ? 0 : this.worm.angle;
        const f = [Math.cos(a), Math.sin(a)];
        const perp = [-f[1], f[0]];
        for (const side of [-1, 1]) {
          const ex = p.x + f[0] * radius * 0.42 + perp[0] * side * radius * 0.42;
          const ey = p.y + f[1] * radius * 0.42 + perp[1] * side * radius * 0.42;
          g.fillStyle(0xffffff, 1).fillCircle(ex, ey, radius * 0.24);
          g.fillStyle(0x1a1230, 1).fillCircle(ex + f[0] * radius * 0.1, ey + f[1] * radius * 0.1, radius * 0.11);
        }
      } else if (carries) {
        this.beadLabels[letterIndex]?.setText(steps[letterIndex] ?? '').setPosition(p.x, p.y);
      }
    }
  }

  private lerpPoint(a: Pt, b: Pt, t: number): Pt {
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }

  private clearBoardArt(): void {
    this.tileArt.forEach((a) => { a.disc.destroy(); a.label.destroy(); });
    this.tileArt.clear();
    this.picture?.destroy();
    this.picture = null;
  }

  private drawHud(): void {
    for (let i = 0; i < WORM.startBooks; i++) {
      this.bookIcons.push(this.add.text(44 + i * 48, 92, '📚', {
        fontFamily: FONT_STACK, fontSize: '34px',
      }).setOrigin(0.5).setDepth(50));
    }
    this.rungText = this.add.text(44, 134, '', {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#9fb2cc',
    }).setOrigin(0, 0.5).setDepth(50);

    this.slotText = this.add.text(GAME_WIDTH / 2, 160, '', {
      fontFamily: scriptFontFamily(this.lang), fontSize: '42px', color: '#ffd23f', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(50);

    const back = this.add.text(12, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 20, y: 12 },
    }).setOrigin(0, 0).setDepth(50);
    back.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(-back.x, -back.y, back.width + back.x + 10, back.height + back.y + 10),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    addRoundedLabelBg(this, back);
    back.on('pointerover', () => back.setColor('#ffffff'));
    back.on('pointerout', () => back.setColor('#dfe6ee'));
    // Straight out, no confirm — the decision Pairs settled and every treat has kept.
    back.on('pointerdown', () => this.scene.start('Menu'));
  }

  /**
   * Nothing in this mode starts without permission: the worm sits still until the child taps, which
   * is the one screen `recipe-mode.md` §5.3 proves you cannot do without.
   */
  private showHowTo(onStart: () => void): void {
    const layer = this.add.container(0, 0).setDepth(200);
    const shade = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85);
    shade.setInteractive();
    const panel = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.8, GAME_HEIGHT * 0.7, 0x0d1030)
      .setStrokeStyle(4, 0x7fe7ff);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 180, `🪱  ${i18next.t('worm.title')}`, {
      fontFamily: FONT_STACK, fontSize: '58px', color: '#7fe7ff', fontStyle: 'bold',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, i18next.t('worm.help'), {
      fontFamily: FONT_STACK, fontSize: '30px', color: '#ffffff', align: 'center',
      wordWrap: { width: GAME_WIDTH * 0.66 }, lineSpacing: 10,
    }).setOrigin(0.5);
    const books = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 90, '📚'.repeat(WORM.startBooks), {
      fontFamily: FONT_STACK, fontSize: '40px',
    }).setOrigin(0.5);
    const start = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 190, i18next.t('worm.start'), {
      fontFamily: FONT_STACK, fontSize: '36px', color: '#0d1030', backgroundColor: '#7fe7ff',
      padding: { x: 40, y: 14 }, fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    layer.add([shade, panel, title, body, books, start]);

    // Behind a timestamp guard, so the tap that spent the Worm cannot skip the card it opened.
    const openedAt = performance.now();
    const dismiss = (): void => {
      if (performance.now() - openedAt < 350) return;
      this.input.off('pointerdown', dismiss);
      layer.destroy(true);
      onStart();
    };
    this.input.on('pointerdown', dismiss);
  }
}
